import React from 'react';
import type { EnquiryFormData, SelectedVase } from '../../../types';
import type {
  CanvasSectionBlock,
  HomepageDesignSystemSettings,
  HomepageSectionSettings,
} from '../sectionRegistry';
import {
  TOTAL_COLUMNS,
} from '../layoutEngine/blockFrame';
import SnapGuides from '../layoutEngine/SnapGuides';
import { SelectionGroupBounds } from '../layoutEngine/SelectionGroupBounds';
import type { EditorBoundsBox } from '../layoutEngine/editorBounds';
import type { AlignmentGuides } from '../layoutEngine/types';
import { shouldSuppressBuilderHoverChrome } from '../layoutEngine/blockHoverChrome';
import ModelPreviewModal from '../../ModelPreviewModal';
import type { RichTextFontOption } from '../../admin/pageLayout/fields';
import type { DesignSystemTypography } from '../../../lib/typography';
import { SectionElementCanvasBlock } from './SectionElementCanvasBlock';

type SectionElementLayerCanvasProps = {
  sectionId?: string;
  settings?: HomepageSectionSettings;
  designSystemOverride?: HomepageDesignSystemSettings;
  enquiryEmailTo?: string;
  onSelectVase?: (vase: SelectedVase) => void;
  onEnquirySubmit?: (formData: EnquiryFormData) => void;
  onNavigateToPage?: (href: string, meta?: { pageKey?: string }) => void;
  className?: string;
  adminPreview?: any;
  flow: any;
  isAdmin: boolean;
  isInteractPreview: boolean;
  isImmersivePreview: boolean;
  isImageAdjustMode: boolean;
  activeAdjustBlockId: string | null;
  moveMode: boolean;
  scaleMode: boolean;
  showSelectionChrome: boolean;
  layerRef: React.RefObject<HTMLDivElement>;
  renderBlocks: CanvasSectionBlock[];
  selectedBlockId: string | null;
  selectedBlockIds: Set<string>;
  activeProjectionRows: number;
  alignmentGuides: AlignmentGuides;
  isTransforming: boolean;
  isPlacementDrag?: boolean;
  editorBoundsByBlockId?: Record<string, EditorBoundsBox>;
  beginTransform: any;
  moveTransform: any;
  endTransform: any;
  beginScale: any;
  moveScale: any;
  endScale: any;
  activeModelPreview: { modelPath: string; vaseId?: number; title?: string } | null;
  setActiveModelPreview: React.Dispatch<React.SetStateAction<any>>;
  activeImageInteraction: any;
  beginSurfaceMove: any;
  moveSurfaceMove: any;
  endSurfaceMove: any;
  handleWheelZoom: any;
  beginHandleInteraction: any;
  moveHandleInteraction: any;
  endHandleInteraction: any;
  editingTextBlockId: string | null;
  setTextMeasureNode: (blockId: string, node: HTMLElement | null) => void;
  richTextFontOptions: RichTextFontOption[];
  typography: DesignSystemTypography;
  fontLibrary: Record<string, string>;
  useFluidSectionTypography: boolean;
  usesFixedFrameText: boolean;
  productRecords: any[];
  selectedProductIds: Set<number>;
  layerSize: { width: number; height: number };
  columnWidthPx: number;
  rowHeightPx: number;
  hoveredProductCardId: string | null;
  setHoveredProductCardId: React.Dispatch<React.SetStateAction<string | null>>;
  productCardRuntimeSnapshotsRef: React.MutableRefObject<Record<string, Record<string, unknown>>>;
  productCardWrapperNodesRef: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  productCardArticleNodesRef: React.MutableRefObject<Record<string, HTMLElement | null>>;
  productCardOverlayNodesRef: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  setProductCardMeasureNode: (blockId: string, node: HTMLElement | null) => void;
  setConnectedBlockDebugSnapshot: (snapshot: Record<string, unknown> | null) => void;
  visibleColourCombinations: any[];
  viewerRandomSelections: Record<string, any>;
  renderLogicNodeIdTag: (block: CanvasSectionBlock) => React.ReactNode;
  renderSelectionOverlay: (
    block: CanvasSectionBlock,
    overlayStyle?: React.CSSProperties,
    overlayRef?: React.Ref<HTMLDivElement>,
    options?: { topChrome?: boolean; canvasLayer?: boolean }
  ) => React.ReactNode;
  renderHoverVisualOutline: (block: CanvasSectionBlock) => React.ReactNode;
  hoveredBlockId: string | null;
  setHoveredBlockId: React.Dispatch<React.SetStateAction<string | null>>;
};

export function SectionElementLayerCanvas({
  sectionId,
  settings,
  designSystemOverride,
  enquiryEmailTo,
  onSelectVase,
  onEnquirySubmit,
  onNavigateToPage,
  className,
  adminPreview,
  flow,
  isAdmin,
  isInteractPreview,
  isImmersivePreview,
  isImageAdjustMode,
  activeAdjustBlockId,
  moveMode,
  scaleMode,
  showSelectionChrome,
  layerRef,
  renderBlocks,
  selectedBlockId,
  selectedBlockIds,
  activeProjectionRows,
  alignmentGuides,
  isTransforming,
  isPlacementDrag = false,
  editorBoundsByBlockId = {},
  beginTransform,
  moveTransform,
  endTransform,
  beginScale,
  moveScale,
  endScale,
  activeModelPreview,
  setActiveModelPreview,
  activeImageInteraction,
  beginSurfaceMove,
  moveSurfaceMove,
  endSurfaceMove,
  handleWheelZoom,
  beginHandleInteraction,
  moveHandleInteraction,
  endHandleInteraction,
  editingTextBlockId,
  setTextMeasureNode,
  richTextFontOptions,
  typography,
  fontLibrary,
  useFluidSectionTypography,
  usesFixedFrameText,
  productRecords,
  selectedProductIds,
  layerSize,
  columnWidthPx,
  rowHeightPx,
  hoveredProductCardId,
  setHoveredProductCardId,
  productCardRuntimeSnapshotsRef,
  productCardWrapperNodesRef,
  productCardArticleNodesRef,
  productCardOverlayNodesRef,
  setProductCardMeasureNode,
  setConnectedBlockDebugSnapshot,
  visibleColourCombinations,
  viewerRandomSelections,
  renderLogicNodeIdTag,
  renderSelectionOverlay,
  renderHoverVisualOutline,
  hoveredBlockId,
  setHoveredBlockId,
}: SectionElementLayerCanvasProps) {
  if (!renderBlocks.length) return null;

  const selectedBlocksForTopChrome =
    showSelectionChrome
      ? renderBlocks.filter((block) => selectedBlockId === block.id || selectedBlockIds.has(block.id))
      : [];
  const hoveredBlock =
    hoveredBlockId ? renderBlocks.find((block) => block.id === hoveredBlockId) : null;
  const showHoverChrome =
    showSelectionChrome &&
    moveMode &&
    hoveredBlock &&
    !shouldSuppressBuilderHoverChrome(hoveredBlock) &&
    selectedBlockId !== hoveredBlock.id &&
    !selectedBlockIds.has(hoveredBlock.id);

  return (
    <div
      ref={layerRef}
      className={`pointer-events-none absolute inset-0 z-[25] ${className ?? ''} ${
        isPlacementDrag ? 'cursor-grabbing' : ''
      }`}
      data-builder-editor-layer="true"
      data-builder-projection-rows={activeProjectionRows}
      data-builder-placement-drag={isPlacementDrag ? 'true' : undefined}
    >
      <ModelPreviewModal
        open={activeModelPreview !== null}
        onClose={() => setActiveModelPreview(null)}
        modelPath={activeModelPreview?.modelPath}
        vaseId={activeModelPreview?.vaseId}
        title={activeModelPreview?.title ?? '3D model preview'}
      />
      {showSelectionChrome && adminPreview?.showPlacementGrid ? (
        <>
          <div
            className="pointer-events-none absolute inset-0 z-[1] rounded-xl"
            style={{
              backgroundImage:
                'linear-gradient(to right, rgba(255,255,255,0.48) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.42) 1px, transparent 1px), linear-gradient(to right, rgba(15,23,42,0.26) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.22) 1px, transparent 1px)',
              backgroundSize: `${100 / TOTAL_COLUMNS}% 100%, 100% ${100 / activeProjectionRows}%, ${100 / TOTAL_COLUMNS}% 100%, 100% ${100 / activeProjectionRows}%`,
              opacity: isInteractPreview ? 0.1 : 0.32,
            }}
          />
          <div
            className="pointer-events-none absolute inset-0 z-[2] rounded-xl"
            style={{
              backgroundImage:
                'linear-gradient(to right, rgba(255,255,255,0.42) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.34) 1px, transparent 1px)',
              backgroundSize: `${100 / TOTAL_COLUMNS}% 100%, 100% ${100 / activeProjectionRows}%`,
              mixBlendMode: 'difference',
              opacity: isInteractPreview ? 0.1 : 0.32,
            }}
          />
        </>
      ) : null}

      {renderBlocks.map((block) => (
        <SectionElementCanvasBlock
          key={block.id}
          block={block}
          sectionId={sectionId}
          settings={settings}
          designSystemOverride={designSystemOverride}
          enquiryEmailTo={enquiryEmailTo}
          onSelectVase={onSelectVase}
          onEnquirySubmit={onEnquirySubmit}
          onNavigateToPage={onNavigateToPage}
          adminPreview={adminPreview}
          flow={flow}
          isAdmin={isAdmin}
          isInteractPreview={isInteractPreview}
          isImmersivePreview={isImmersivePreview}
          isImageAdjustMode={isImageAdjustMode}
          activeAdjustBlockId={activeAdjustBlockId}
          moveMode={moveMode}
          scaleMode={scaleMode}
          activeProjectionRows={activeProjectionRows}
          activeImageInteraction={activeImageInteraction}
          beginSurfaceMove={beginSurfaceMove}
          moveSurfaceMove={moveSurfaceMove}
          endSurfaceMove={endSurfaceMove}
          handleWheelZoom={handleWheelZoom}
          beginHandleInteraction={beginHandleInteraction}
          moveHandleInteraction={moveHandleInteraction}
          endHandleInteraction={endHandleInteraction}
          editingTextBlockId={editingTextBlockId}
          setTextMeasureNode={setTextMeasureNode}
          richTextFontOptions={richTextFontOptions}
          typography={typography}
          fontLibrary={fontLibrary}
          useFluidSectionTypography={useFluidSectionTypography}
          usesFixedFrameText={usesFixedFrameText}
          productRecords={productRecords}
          selectedProductIds={selectedProductIds}
          layerSize={layerSize}
          columnWidthPx={columnWidthPx}
          rowHeightPx={rowHeightPx}
          hoveredProductCardId={hoveredProductCardId}
          setHoveredProductCardId={setHoveredProductCardId}
          productCardRuntimeSnapshotsRef={productCardRuntimeSnapshotsRef}
          productCardWrapperNodesRef={productCardWrapperNodesRef}
          productCardArticleNodesRef={productCardArticleNodesRef}
          productCardOverlayNodesRef={productCardOverlayNodesRef}
          setProductCardMeasureNode={setProductCardMeasureNode}
          setConnectedBlockDebugSnapshot={setConnectedBlockDebugSnapshot}
          visibleColourCombinations={visibleColourCombinations}
          viewerRandomSelections={viewerRandomSelections}
          selectedBlockId={selectedBlockId}
          selectedBlockIds={selectedBlockIds}
          showLayoutFrames={adminPreview?.showLayoutFrames === true}
          showSelectionChrome={showSelectionChrome}
          hoveredBlockId={hoveredBlockId}
          setHoveredBlockId={setHoveredBlockId}
          setActiveModelPreview={setActiveModelPreview}
          renderLogicNodeIdTag={renderLogicNodeIdTag}
          renderSelectionOverlay={renderSelectionOverlay}
        />
      ))}
      {showHoverChrome && hoveredBlock ? (
        <div
          key={`hover-chrome-${hoveredBlock.id}`}
          className="pointer-events-none absolute inset-0 z-[9999]"
          data-builder-hover-chrome="true"
        >
          {renderHoverVisualOutline(hoveredBlock)}
        </div>
      ) : null}
      {selectedBlocksForTopChrome.map((block) => (
        <div
          key={`top-selection-${block.id}`}
          className="pointer-events-none absolute inset-0 z-[10000]"
          data-builder-selection-chrome="true"
        >
          {renderSelectionOverlay(block, undefined, undefined, { topChrome: true, canvasLayer: true })}
        </div>
      ))}

      {showSelectionChrome && selectedBlocksForTopChrome.length > 1 && !isPlacementDrag ? (
        <SelectionGroupBounds
          blocks={selectedBlocksForTopChrome}
          editorBoundsByBlockId={editorBoundsByBlockId}
          columnWidthPx={columnWidthPx}
          rowHeightPx={rowHeightPx}
        />
      ) : null}

      {isPlacementDrag ? (
        <div
          className="pointer-events-none absolute bottom-3 left-3 z-[10001] rounded-lg border border-blue-200/90 bg-white/95 px-3 py-2 text-[11px] font-medium text-stone-800 shadow-md"
          data-builder-placement-hint="true"
        >
          <span className="block">Click to place · Esc cancel · Enter place · Arrow keys nudge</span>
        </div>
      ) : null}

      <SnapGuides
        guides={alignmentGuides}
        active={moveMode && isTransforming}
        showGapGuides={adminPreview?.showGapGuides ?? false}
      />
    </div>
  );
}
